'use client';

import { trpc } from '@/app/api/trpc/client';
import { Dropdown } from '@/app/components/ui/Dropdown';
import type { Sport } from '@trainme/db/client';
import { HiCheck, HiOutlineChevronDown } from 'react-icons/hi';
import { cn } from '@/utils/uiUtils';

type Props = {
  value: string;

  onChange: (selectedSportId: string) => void;
};

export default function SportTypeSelect({ value, onChange }: Props) {
  const {
    data: sportTypes,
    isLoading,
    isError,
  } = trpc.sports.getSportTypes.useQuery<Sport[]>(undefined, { enabled: true });

  const handleSelect = (selectedSportId: string) => {
    onChange(selectedSportId);
  };

  const selectedLabel =
    sportTypes?.find((sportType) => sportType.sportType === value)?.sportType ??
    '';

  const buttonLabel = isLoading
    ? 'Loading...'
    : isError
      ? 'No sport types available'
      : selectedLabel || 'Select a sport type';

  return (
    <div className='w-full'>
      <Dropdown
        placement='bottom-start'
        disabled={isLoading || isError}
        matchTriggerWidth={false}
        menuClassName='min-w-[18rem]'
        trigger={
          <button
            type='button'
            className={cn(
              'flex w-full h-9 items-center px-4 text-[0.9em] text-left rounded-lg bg-stone-300 dark:bg-stone-700 text-gray-900 dark:text-gray-100 border border-stone-400 dark:border-stone-700 hover:bg-stone-200 dark:hover:bg-stone-600 focus:outline-none focus:ring-2 focus:ring-stone-500/40 disabled:opacity-50 disabled:cursor-not-allowed'
            )}
          >
            <span className='flex w-full items-center justify-between gap-2'>
              <span className='min-w-0 truncate'>{buttonLabel}</span>
              <HiOutlineChevronDown className='h-4 w-4 flex-shrink-0 opacity-70' />
            </span>
          </button>
        }
      >
        {sportTypes?.map((sportType: Sport) => (
          <Dropdown.Item
            key={sportType.id}
            onClick={() => handleSelect(sportType.sportType)}
            active={sportType.sportType === value}
          >
            <span className='flex items-center justify-between gap-2 w-full'>
              <span className='whitespace-nowrap'>{sportType.sportType}</span>
              {sportType.sportType === value && (
                <HiCheck className='h-4 w-4 flex-shrink-0 opacity-80' />
              )}
            </span>
          </Dropdown.Item>
        ))}
      </Dropdown>
    </div>
  );
}
